/**
 * client/src/components/layout/credential-added-push-listener.tsx
 *
 * #3169 — mounts `useCredentialAddedPush` once (app-shell.tsx, next to
 * `RemovalHoldPushListener`). On a frame it:
 *  1. Invalidates the notifications feed query so the durable row (module 01
 *     Task 2/3) appears without a manual refresh.
 *  2. Raises a `toast.notice` carrying the frame's message (or the shared
 *     fallback copy — never re-typed here) plus, only when the server-minted
 *     `href` passes the fail-closed check below, a followable link to it.
 *
 * Mirrors `ladder-downgrade-push-listener.tsx`'s single-event shape
 * (Reuse-First Check).
 */

import { useQueryClient } from "@tanstack/react-query";
import { useCredentialAddedPush, type CredentialAddedFrame } from "@/lib/use-credential-added-push";
import { toast } from "@/components/ui/toast";
import { NOTIFICATIONS_QUERY_KEY } from "@/lib/use-notifications";
import { CREDENTIAL_ADDED_COPY } from "@shared/credential-append-copy";
import { validateReturnTo } from "@/lib/return-to";

/**
 * Fail-closed link validation (Round-1 review BLOCK 1 / MAJOR 2 / MAJOR 3):
 * the server mints `href`, so it is never trusted as-is, and a WS frame's
 * fields are only `unknown` at runtime regardless of their compile-time
 * type. Reuses the repo's own same-origin/no-scheme/length-bound validator
 * (`validateReturnTo`, Reuse-First Check) and layers an EXACT-route check on
 * top — prefix matching alone would wrongly accept a lookalike route
 * (`/security-keys-evil`) or a traversal path
 * (`/security-keys/../admin`) that merely starts with the allowed string.
 */
function safeInAppPath(href: unknown, allowedRoute: string): string | undefined {
  if (typeof href !== "string") return undefined;
  const validated = validateReturnTo(href);
  if (!validated) return undefined;
  if (validated !== allowedRoute && !validated.startsWith(`${allowedRoute}?`)) return undefined;
  return validated;
}

export function CredentialAddedPushListener(): null {
  const queryClient = useQueryClient();
  useCredentialAddedPush((frame: CredentialAddedFrame) => {
    void queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
    const message = typeof frame.message === "string" ? frame.message : CREDENTIAL_ADDED_COPY.owner;
    const href = safeInAppPath(frame.href, "/security-keys");
    toast.notice(message, { href, linkLabel: href ? CREDENTIAL_ADDED_COPY.linkLabel : undefined });
  });
  return null;
}
